/**
 * Counting Nature - Bilingual learn & quiz modes
 * Learn: 1-20 with nature emojis, click to hear
 * Quiz: "How many X do you see?" with multiple choice
 */

import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  floraFaunaData,
  numberWordsEn,
  numberWordsEs,
} from '../../data/countingNature';
import {
  speak as speakNatural,
  stop as stopNatural,
  playClickFeedback,
} from '../../lib/naturalVoiceService';

// Shared design tokens with AnimalAlphabetGrid — keep games visually unified.
const PASTEL_COLORS = [
  '#dbeef5',
  '#fce8d2',
  '#dcecda',
  '#fadcdc',
  '#e8defc',
  '#d6f0e0',
];
const INK_GREEN = '#2a6d5b';
const BRAND_GREEN = '#42a57a';
const SERIF = "'Fraunces', Georgia, serif";
const SANS = "'Inter', system-ui, -apple-system, sans-serif";

interface CountingNatureGameProps {
  onGoAlphabet?: () => void;
  onGoNames?: () => void;
}

export function CountingNatureGame({ onGoAlphabet, onGoNames }: CountingNatureGameProps) {
  const { language } = useLanguage();
  const [mode, setMode] = useState<'learn' | 'quiz'>('learn');
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [numbersRevealed, setNumbersRevealed] = useState(false);

  const isEn = language === 'en';
  const numberWords = isEn ? numberWordsEn : numberWordsEs;

  const content = {
    en: {
      title: 'Count with Nature',
      subtitle: 'Learn to count from 1 to 20',
      welcome: 'Welcome, Little Counter!',
      welcomeDesc: "Let's count beautiful flowers, butterflies, and animals.",
      learnMode: 'Learn',
      quizMode: 'Quiz',
      score: 'Score',
      howMany: 'How many do you see?',
      correct: "Amazing! That's right.",
      incorrect: 'Not quite. The answer is',
      tryNext: 'Try the next one.',
      nextQuestion: 'Next Question',
      alphabet: 'Alphabet',
      names: 'Names',
    },
    es: {
      title: 'Cuenta con la Naturaleza',
      subtitle: 'Aprende a contar del 1 al 20',
      welcome: '¡Bienvenido, Pequeño Contador!',
      welcomeDesc: 'Vamos a contar hermosas flores, mariposas y animales.',
      learnMode: 'Aprender',
      quizMode: 'Quiz',
      score: 'Puntuación',
      howMany: '¿Cuántos ves?',
      correct: '¡Increíble! Correcto.',
      incorrect: 'Casi. La respuesta es',
      tryNext: 'Intenta la siguiente.',
      nextQuestion: 'Siguiente Pregunta',
      alphabet: 'Alfabeto',
      names: 'Nombres',
    },
  };

  const t = content[language];

  useEffect(() => {
    setNumbersRevealed(false);
    const prompt = `${t.welcome} ${t.welcomeDesc}`;
    speakNatural({
      text: prompt,
      language: language as 'en' | 'es',
      onComplete: () => setNumbersRevealed(true),
    });
    return () => {
      stopNatural();
    };
  }, [language]);

  const speakNumber = useCallback(
    (num: number, item: (typeof floraFaunaData)[0]) => {
      playClickFeedback();
      const name = num === 1
        ? (isEn ? item.nameEn : item.nameEs)
        : (isEn ? item.pluralEn : item.pluralEs);
      const text = `${numberWords[num]}. ${num} ${name}`;

      speakNatural({
        text,
        language: language as 'en' | 'es',
      });
    },
    [isEn, language, numberWords]
  );

  const generateQuestion = useCallback(() => {
    const answer = Math.floor(Math.random() * 20) + 1;
    const item = floraFaunaData[Math.floor(Math.random() * floraFaunaData.length)];
    const options = [answer];
    while (options.length < 6) {
      const opt = Math.floor(Math.random() * 20) + 1;
      if (!options.includes(opt)) options.push(opt);
    }
    return { answer, item, options: options.sort(() => Math.random() - 0.5) };
  }, []);

  const [quizState, setQuizState] = useState(() => generateQuestion());

  const checkAnswer = useCallback(
    (selected: number, correct: number) => {
      if (answered) return;
      setAnswered(true);
      setTotalQuestions((p) => p + 1);

      if (selected === correct) {
        setScore((s) => s + 1);
        setFeedbackCorrect(true);
        setFeedback(t.correct);
        playClickFeedback();

        speakNatural({
          text: isEn ? `Correct! The answer is ${correct}!` : `¡Correcto! La respuesta es ${correct}!`,
          language: language as 'en' | 'es',
        });
      } else {
        setFeedbackCorrect(false);
        setFeedback(`${t.incorrect} ${correct}. ${t.tryNext}`);
      }

      setShowNext(true);
    },
    [answered, isEn, language, t]
  );

  const nextQuestion = useCallback(() => {
    const next = generateQuestion();
    setQuizState(next);
    setFeedback('');
    setShowNext(false);
    setAnswered(false);
  }, [generateQuestion]);

  const switchToQuiz = useCallback(() => {
    setMode('quiz');
    const q = generateQuestion();
    setQuizState(q);
    setScore(0);
    setTotalQuestions(0);
    setFeedback('');
    setShowNext(false);
    setAnswered(false);
  }, [generateQuestion]);

  const switchToLearn = useCallback(() => {
    setMode('learn');
  }, []);

  // Shared button styles for primary (brand-green filled) / secondary (outline) pills
  const primaryPill = (active = true): React.CSSProperties => ({
    background: active ? BRAND_GREEN : 'transparent',
    color: active ? '#ffffff' : BRAND_GREEN,
    border: `1.5px solid ${BRAND_GREEN}`,
    fontFamily: SANS,
    fontWeight: 600,
    fontSize: 18,
    padding: '12px 24px',
    borderRadius: 999,
    cursor: 'pointer',
  });
  const secondaryPill: React.CSSProperties = {
    background: 'transparent',
    color: BRAND_GREEN,
    border: `1.5px solid ${BRAND_GREEN}`,
    fontFamily: SANS,
    fontWeight: 600,
    fontSize: 18,
    padding: '12px 24px',
    borderRadius: 999,
    cursor: 'pointer',
  };

  return (
    <div className="min-h-screen p-4 md:p-6" style={{ background: '#fbfbe8' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header — same Fraunces serif (light) + Inter pattern as Alphabet */}
        <div className="text-center mb-6 md:mb-8">
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-7xl mb-2"
            style={{
              fontFamily: SERIF,
              fontWeight: 300,
              color: INK_GREEN,
              letterSpacing: '-0.015em',
            }}
          >
            {t.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-2xl md:text-4xl"
            style={{ fontFamily: SANS, fontWeight: 400, color: '#355c46' }}
          >
            {t.subtitle}
          </motion.p>
        </div>

        {/* Mode selector + sibling navigation */}
        <motion.div
          initial={false}
          animate={{ opacity: numbersRevealed ? 1 : 0, pointerEvents: numbersRevealed ? 'auto' : 'none' }}
          transition={{ duration: 0.3 }}
          className="flex justify-center gap-3 mb-6 flex-wrap"
        >
          <motion.button
            onClick={switchToLearn}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.98 }}
            style={primaryPill(mode === 'learn')}
          >
            {t.learnMode}
          </motion.button>
          <motion.button
            onClick={switchToQuiz}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.98 }}
            style={primaryPill(mode === 'quiz')}
          >
            {t.quizMode}
          </motion.button>
          {onGoAlphabet && (
            <motion.button
              onClick={onGoAlphabet}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              style={secondaryPill}
            >
              {t.alphabet}
            </motion.button>
          )}
          {onGoNames && (
            <motion.button
              onClick={onGoNames}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              style={secondaryPill}
            >
              {t.names}
            </motion.button>
          )}
        </motion.div>

        {/* Learn Mode */}
        {mode === 'learn' && (
          <motion.div
            initial={false}
            animate={{ opacity: numbersRevealed ? 1 : 0, pointerEvents: numbersRevealed ? 'auto' : 'none' }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 md:gap-6 lg:gap-8"
          >
            {floraFaunaData.map((item, i) => {
              const num = i + 1;
              const pastel = PASTEL_COLORS[i % PASTEL_COLORS.length];
              const label =
                num === 1
                  ? isEn ? item.nameEn : item.nameEs
                  : isEn ? item.pluralEn : item.pluralEs;

              return (
                <motion.div
                  key={num}
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={numbersRevealed ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.92 }}
                  transition={{ delay: numbersRevealed ? i * 0.03 : 0, duration: 0.35 }}
                  onClick={() => speakNumber(num, item)}
                  whileHover={{ y: -2 }}
                  className="group cursor-pointer"
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e8e0c8',
                    borderRadius: 18,
                    padding: 14,
                    transition: 'transform 200ms ease, box-shadow 200ms ease',
                  }}
                >
                  {/* Pastel inner tile with serif numeral + count of emojis */}
                  <div
                    style={{
                      background: pastel,
                      borderRadius: 14,
                      padding: '36px 16px 24px',
                      textAlign: 'center',
                    }}
                  >
                    <span
                      className="block"
                      style={{
                        fontFamily: SERIF,
                        fontSize: 'clamp(80px, 11vw, 132px)',
                        fontWeight: 600,
                        color: INK_GREEN,
                        lineHeight: 1,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {num}
                    </span>
                    <div
                      className="flex flex-wrap justify-center gap-1.5 mt-4"
                      style={{ minHeight: 60 }}
                    >
                      {Array.from({ length: num }, (_, j) => (
                        <span
                          key={j}
                          style={{
                            fontSize: 28,
                            lineHeight: 1,
                            animation: 'float 3s ease-in-out infinite',
                            animationDelay: `${j * 50}ms`,
                          }}
                        >
                          {item.emoji}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Number + label, italic serif */}
                  <div
                    style={{
                      fontFamily: SERIF,
                      fontStyle: 'italic',
                      fontSize: 22,
                      fontWeight: 500,
                      color: INK_GREEN,
                      textAlign: 'center',
                      marginTop: 14,
                    }}
                  >
                    {num} {label}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Quiz Mode */}
        {mode === 'quiz' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: numbersRevealed ? 1 : 0, pointerEvents: numbersRevealed ? 'auto' : 'none' }}
            transition={{ delay: numbersRevealed ? 0.2 : 0, duration: 0.4 }}
            className="space-y-6 max-w-3xl mx-auto"
          >
            <div
              className="rounded-2xl py-3 px-6 text-center"
              style={{
                background: '#ffffff',
                border: '1px solid #e8e0c8',
                fontFamily: SANS,
                fontSize: 20,
                fontWeight: 600,
                color: INK_GREEN,
              }}
            >
              {t.score}: {score} / {totalQuestions}
            </div>

            <div
              className="rounded-3xl p-8 md:p-10"
              style={{ background: '#ffffff', border: '1px solid #e8e0c8' }}
            >
              <p
                className="text-center mb-6"
                style={{
                  fontFamily: SERIF,
                  fontSize: 'clamp(28px, 4vw, 40px)',
                  fontWeight: 400,
                  color: INK_GREEN,
                  letterSpacing: '-0.01em',
                }}
              >
                {isEn
                  ? `How many ${quizState.item.nameEn}${quizState.answer !== 1 ? 's' : ''} do you see?`
                  : `¿Cuántos ${quizState.answer === 1 ? quizState.item.nameEs : quizState.item.pluralEs} ves?`}
              </p>

              <div
                className="flex flex-wrap justify-center gap-3 p-6 rounded-2xl mb-8"
                style={{ background: '#fbfbe8' }}
              >
                {Array.from({ length: quizState.answer }, (_, i) => (
                  <span key={i} style={{ fontSize: 56, lineHeight: 1 }}>
                    {quizState.item.emoji}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {quizState.options.map((opt) => (
                  <motion.button
                    key={opt}
                    onClick={() => checkAnswer(opt, quizState.answer)}
                    disabled={answered}
                    whileHover={{ scale: answered ? 1 : 1.04 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      padding: '20px 0',
                      borderRadius: 16,
                      fontFamily: SERIF,
                      fontSize: 'clamp(36px, 5vw, 56px)',
                      fontWeight: 600,
                      border: 'none',
                      cursor: answered ? 'default' : 'pointer',
                      background: answered
                        ? opt === quizState.answer
                          ? BRAND_GREEN
                          : '#eee8d8'
                        : BRAND_GREEN,
                      color: answered
                        ? opt === quizState.answer
                          ? '#ffffff'
                          : '#a9a394'
                        : '#ffffff',
                      opacity: answered && opt !== quizState.answer ? 0.7 : 1,
                    }}
                  >
                    {opt}
                  </motion.button>
                ))}
              </div>

              {feedback && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 text-center"
                  style={{
                    fontFamily: SERIF,
                    fontStyle: 'italic',
                    fontSize: 28,
                    fontWeight: 500,
                    color: feedbackCorrect ? INK_GREEN : '#b85a55',
                    minHeight: 40,
                  }}
                >
                  {feedback}
                </motion.p>
              )}

              {showNext && (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onClick={nextQuestion}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="mt-6 w-full"
                  style={{
                    padding: '16px 0',
                    borderRadius: 999,
                    background: BRAND_GREEN,
                    color: '#ffffff',
                    fontFamily: SANS,
                    fontSize: 20,
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {t.nextQuestion}
                </motion.button>
              )}
            </div>
          </motion.div>
        )}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}
